/**
 * RequestToolsMenu, the compact footer tools control (#1149 Issue 9).
 *
 * On a phone the Request Detail / Create Request footer is one non-wrapping
 * row. Once text intake added a fourth control the row needed ~400pt against
 * 331pt of usable width, and because nothing shrinks and every ancestor sets
 * `overflow: hidden`, the excess was CLIPPED: Submit simply disappeared with
 * no way to reach it.
 *
 * So on compact layouts the intake tools, Archive and (on a Draft) Close
 * collapse behind one control. Close moved in later: with it still in the
 * row, a Draft needed ~328pt against ~287pt at 375pt with the section rail,
 * and the right-hand group slid over Edit until it read "Ed". The row is now
 * `[⋯] [Edit] .... [Send to Vertiv]`, about 246pt.
 *
 * The trigger is a `⋯` in a rounded square, not the "+" it used to be. The "+"
 * was chosen because two of three items added source material; with Close
 * and Archive in the menu, half of it is request actions, and a "+" would
 * misname what it opens. `⋯` is the platform's "more actions" glyph, and the
 * square outline matches the Close button's `#666` outline it sits beside.
 * The panel still separates the two kinds of item: "Add to request" above,
 * a divider, then "Request actions".
 *
 * The panel is one animated bottom sheet on every surface where the trigger
 * renders (native, and web at 640px or narrower). Motion is core `Animated`:
 * backdrop fades, sheet slides up from its own height, and it dismisses by
 * backdrop tap, Android back, a drag down (native), or the close button and
 * Escape (web), all through the same animated close. Reduced motion swaps
 * the slide for a short fade.
 *
 * An item's action runs only after the sheet has fully gone. On iOS an RN
 * Modal owns its own UIWindow, and an action that presents another modal
 * while this one is still up is the modal-over-modal conflict described in
 * `.claude/rules/cross-platform-ui.md`. So the action waits for
 * `Modal.onDismiss` on iOS and for the exit animation elsewhere, with a
 * fallback timer in case neither signal arrives.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AccessibilityInfo,
  Animated,
  Easing,
  Modal,
  PanResponder,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
  type LayoutChangeEvent,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { colors } from '../../theme/colors';
import { MoreHorizontal, X } from '../icons';

/** Row floor for an item with no hint (label only). */
const MENU_ITEM_HEIGHT = 48;
/**
 * First-frame estimate for an item with a hint. Hints wrap rather than
 * truncate, and at phone widths the longer ones take two lines: 20pt of
 * padding + a 20pt label + two 16pt hint lines.
 */
const MENU_ITEM_WITH_HINT_ESTIMATE = 72;
/** Both section headings plus the divider between them. */
const SECTION_HEADINGS_HEIGHT = 56;
/** Grabber (4pt) and its margins. */
const SHEET_GRABBER_HEIGHT = 14;
/**
 * Web: the close-button row that replaces the grabber. 44pt keeps the button
 * a full touch target on a phone-width browser.
 */
const SHEET_WEB_HEADER_HEIGHT = 44;
/** Top padding inside the sheet, above the grabber. */
const SHEET_TOP_PADDING = 4;
/** Top edge border. RN's `height` includes border and padding. */
const SHEET_BORDER_WIDTH = 1;
/** Bottom padding floor when the device reports no safe-area inset. */
const SHEET_MIN_BOTTOM_PADDING = 16;

/** Open: backdrop fade and sheet slide (ease-out). */
const BACKDROP_ENTER_MS = 200;
const SHEET_ENTER_MS = 250;
/** Close: both run together (ease-in). */
const EXIT_MS = 200;
/** Reduced motion: opacity only, both directions. */
const REDUCED_MOTION_FADE_MS = 150;
/**
 * How long after the exit STARTS the close is forced through if the exit
 * animation never reports back (an interrupted or dropped animation).
 */
const DEFERRED_ACTION_FALLBACK_MS = EXIT_MS + 400;
/**
 * iOS only: how long after the Modal is HIDDEN the close is forced through if
 * `onDismiss` never arrives. Measured from the hide, not the exit start, so a
 * slow JS thread during the exit cannot fire the action while the Modal is
 * still presented. Same safety-net value as `TextIntakeModal`'s pick dance.
 */
const IOS_DISMISS_FALLBACK_MS = 700;
/** Gap kept between the sheet's top and the top safe-area inset. */
const SHEET_TOP_CLEARANCE = 24;

/** A drag further than this, in pt, dismisses on release. */
const DRAG_DISMISS_DISTANCE = 96;
/** A downward flick faster than this, in pt/ms, dismisses on release. */
const DRAG_DISMISS_VELOCITY = 0.9;
/** Movement before the sheet claims the gesture, so taps still land on items. */
const DRAG_CLAIM_THRESHOLD = 6;

const USE_NATIVE_DRIVER = Platform.OS !== 'web';

/**
 * Enter eases out, exit eases in. Resolved defensively because the Jest
 * react-native mock (src/setupTests.ts) has no `Easing`; `timing` then falls
 * back to its default curve, which only the mock ever sees.
 */
const EASE_OUT = Easing?.out?.(Easing.cubic);
const EASE_IN = Easing?.in?.(Easing.cubic);

export interface RequestToolsMenuItem {
  key: string;
  label: string;
  /** Second line under the label, what the action is for. Wraps, never truncates. */
  hint?: string;
  icon: React.ReactNode;
  onPress: () => void;
  /** Colour only: renders the label in the destructive colour. */
  destructive?: boolean;
  /**
   * Which section the item sits in. Defaults to `'actions'` for a destructive
   * item and `'add'` otherwise, so placement and colour stay separate choices.
   */
  group?: 'add' | 'actions';
  /** Rendered dimmed, announced as disabled, and never fires `onPress`. */
  disabled?: boolean;
  testID?: string;
}

interface RequestToolsMenuProps {
  items: RequestToolsMenuItem[];
  /** Square trigger edge, matching the buttons beside it. */
  size?: number;
  disabled?: boolean;
  testID?: string;
}

type SheetPhase = 'closed' | 'opening' | 'open' | 'closing';

function getCreatePortal():
  | ((ui: React.ReactNode, container: Element) => React.ReactNode)
  | null {
  if (Platform.OS !== 'web') {
    return null;
  }
  try {
    return require('react-dom').createPortal as (
      ui: React.ReactNode,
      container: Element,
    ) => React.ReactNode;
  } catch {
    return null;
  }
}

const itemGroup = (item: RequestToolsMenuItem): 'add' | 'actions' =>
  item.group ?? (item.destructive ? 'actions' : 'add');

/**
 * Tracks the OS reduced-motion setting. RN-web maps it to
 * `prefers-reduced-motion`. The Jest react-native mock has no
 * AccessibilityInfo at all, and RN's own mock may return undefined from
 * either call, so every access is guarded.
 */
function useReduceMotion(): boolean {
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    let active = true;
    const pending = AccessibilityInfo?.isReduceMotionEnabled?.();
    if (pending && typeof pending.then === 'function') {
      pending
        .then(value => {
          if (active) {
            setReduceMotion(Boolean(value));
          }
        })
        .catch(() => {});
    }
    const subscription = AccessibilityInfo?.addEventListener?.(
      'reduceMotionChanged',
      (value: boolean) => setReduceMotion(Boolean(value)),
    );
    return () => {
      active = false;
      subscription?.remove?.();
    };
  }, []);
  return reduceMotion;
}

export const RequestToolsMenu: React.FC<RequestToolsMenuProps> = ({
  items,
  size = 40,
  disabled = false,
  testID = 'request-tools-menu',
}) => {
  const insets = useSafeAreaInsets();
  const { height: windowHeight } = useWindowDimensions();
  const reduceMotion = useReduceMotion();
  const isWeb = Platform.OS === 'web';

  /** `mounted` drives Modal `visible` / the portal; `phase` gates re-entry. */
  const [mounted, setMounted] = useState(false);
  const [phase, setPhase] = useState<SheetPhase>('closed');
  const phaseRef = useRef<SheetPhase>('closed');
  const setSheetPhase = useCallback((next: SheetPhase) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);

  const [measuredContentHeight, setMeasuredContentHeight] = useState(0);

  const backdropOpacity = useRef(new Animated.Value(0)).current;
  const sheetOpacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(0)).current;

  /** The item action waiting for the sheet to be gone. Cleared when it runs. */
  const pendingActionRef = useRef<(() => void) | null>(null);
  /** Bumps on every open, so a stale exit callback or timer cannot act on a newer sheet. */
  const generationRef = useRef(0);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountedRef = useRef(false);
  /** Web focus management: the trigger gets focus back, the panel holds it. */
  const triggerRef = useRef<View>(null);
  const panelRef = useRef<View>(null);

  const additions = items.filter(item => itemGroup(item) === 'add');
  const actions = items.filter(item => itemGroup(item) === 'actions');
  // Section headers only earn their space when both groups exist.
  const showHeadings = additions.length > 0 && actions.length > 0;

  const bottomPadding = Math.max(SHEET_MIN_BOTTOM_PADDING, insets.bottom);
  const sheetChrome = SHEET_BORDER_WIDTH + SHEET_TOP_PADDING + bottomPadding;
  /**
   * The sheet is given an explicit height rather than being left to size to
   * its content. Inside the Modal on Android the content-sized container
   * collapsed to zero: the sheet rendered, with the right items, at no height
   * at all, so only the backdrop showed. `minHeight` does not rescue it; Yoga
   * still resolves the container to zero.
   *
   * The estimate allows for wrapped hint rows so the first frame does not
   * clip. The inner content wrapper still lays out at its natural height
   * inside a sheet of known height, so its `onLayout` is safe to trust, and
   * once it reports, the measurement replaces the estimate in both
   * directions: taller when a hint wraps further, shorter when hints fit on
   * one line, so no dead space is left under the last row.
   *
   * Capped below the top safe-area inset. Past the cap (large font scale,
   * more items) the content scrolls instead of being pushed off the top.
   */
  const estimatedContentHeight =
    (isWeb ? SHEET_WEB_HEADER_HEIGHT : SHEET_GRABBER_HEIGHT) +
    (showHeadings ? SECTION_HEADINGS_HEIGHT : 0) +
    items.reduce(
      (sum, item) =>
        sum + (item.hint ? MENU_ITEM_WITH_HINT_ESTIMATE : MENU_ITEM_HEIGHT),
      0,
    );
  const contentHeight =
    measuredContentHeight > 0 ? measuredContentHeight : estimatedContentHeight;
  const maxSheetHeight = Math.max(
    0,
    windowHeight - insets.top - SHEET_TOP_CLEARANCE,
  );
  const naturalSheetHeight = contentHeight + sheetChrome;
  const sheetHeight =
    maxSheetHeight > 0
      ? Math.min(naturalSheetHeight, maxSheetHeight)
      : naturalSheetHeight;
  const contentOverflows = naturalSheetHeight > sheetHeight;
  const contentOverflowsRef = useRef(contentOverflows);
  contentOverflowsRef.current = contentOverflows;
  /**
   * How far below its resting place the sheet starts and ends. The window
   * height, not the sheet height: the first frame's height is an estimate,
   * and if a hint wraps further the measured sheet is taller, so an offset of
   * the estimate would show the sheet's top above the fold before the slide.
   */
  const offscreenOffsetRef = useRef(windowHeight);
  offscreenOffsetRef.current = Math.max(windowHeight, naturalSheetHeight);

  const clearFallbackTimer = useCallback(() => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  /**
   * Ends a close: the sheet is gone, so the phase returns to `closed` (the
   * trigger works again) and the deferred action runs, at most once.
   */
  const settleClose = useCallback(
    (generation: number) => {
      if (
        generation !== generationRef.current ||
        phaseRef.current !== 'closing'
      ) {
        return;
      }
      clearFallbackTimer();
      setSheetPhase('closed');
      setMounted(false);
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      // Web: focus returns to the control that opened the sheet, before the
      // action runs, so a dialog the action opens can take it from there.
      if (Platform.OS === 'web') {
        (triggerRef.current as unknown as HTMLElement | null)?.focus?.();
      }
      if (action && !unmountedRef.current) {
        action();
      }
    },
    [clearFallbackTimer, setSheetPhase],
  );

  /**
   * The exit animation is done. Everywhere except iOS the close settles
   * here. On iOS the Modal is hidden now and the close settles in
   * `onDismiss`, the signal that the presentation slot is free, so the phase
   * stays `closing` (and the trigger inert) until then. Reopening before that
   * signal is what leaves an invisible Modal stuck over the screen.
   */
  const finishExit = useCallback(
    (generation: number) => {
      if (
        generation !== generationRef.current ||
        phaseRef.current !== 'closing'
      ) {
        return;
      }
      if (Platform.OS === 'ios') {
        setMounted(false);
        // Re-arm the safety net from the hide, so it can only fire once the
        // Modal has had time to leave the screen.
        clearFallbackTimer();
        fallbackTimerRef.current = setTimeout(() => {
          fallbackTimerRef.current = null;
          settleClose(generation);
        }, IOS_DISMISS_FALLBACK_MS);
        return;
      }
      settleClose(generation);
    },
    [clearFallbackTimer, settleClose],
  );

  const close = useCallback(
    (action?: () => void) => {
      const current = phaseRef.current;
      if (current === 'closed' || current === 'closing') {
        return;
      }
      pendingActionRef.current = action ?? null;
      setSheetPhase('closing');
      const generation = generationRef.current;

      // Safety net: if neither the animation callback nor `onDismiss` settles
      // the close, the action must still run and the trigger must come back.
      clearFallbackTimer();
      fallbackTimerRef.current = setTimeout(() => {
        fallbackTimerRef.current = null;
        settleClose(generation);
      }, DEFERRED_ACTION_FALLBACK_MS);

      backdropOpacity.stopAnimation();
      sheetOpacity.stopAnimation();
      translateY.stopAnimation();

      const easing = EASE_IN;
      const animations = reduceMotion
        ? [
            Animated.timing(backdropOpacity, {
              toValue: 0,
              duration: REDUCED_MOTION_FADE_MS,
              easing,
              useNativeDriver: USE_NATIVE_DRIVER,
            }),
            Animated.timing(sheetOpacity, {
              toValue: 0,
              duration: REDUCED_MOTION_FADE_MS,
              easing,
              useNativeDriver: USE_NATIVE_DRIVER,
            }),
          ]
        : [
            Animated.timing(backdropOpacity, {
              toValue: 0,
              duration: EXIT_MS,
              easing,
              useNativeDriver: USE_NATIVE_DRIVER,
            }),
            Animated.timing(translateY, {
              toValue: offscreenOffsetRef.current,
              duration: EXIT_MS,
              easing,
              useNativeDriver: USE_NATIVE_DRIVER,
            }),
          ];
      Animated.parallel(animations).start(({ finished }) => {
        if (finished) {
          finishExit(generation);
        }
      });
    },
    [
      backdropOpacity,
      clearFallbackTimer,
      finishExit,
      settleClose,
      reduceMotion,
      setSheetPhase,
      sheetOpacity,
      translateY,
    ],
  );

  const handleTriggerPress = useCallback(() => {
    if (disabled) {
      return;
    }
    const current = phaseRef.current;
    if (current === 'opening' || current === 'open') {
      close();
      return;
    }
    // Mid-exit: the sheet is on its way out and its deferred action has not
    // run yet. Opening a second sheet now would either stack two Modals or
    // leave the old one stuck invisible, so the press is ignored.
    if (current === 'closing') {
      return;
    }
    generationRef.current += 1;
    clearFallbackTimer();
    pendingActionRef.current = null;
    backdropOpacity.setValue(0);
    if (reduceMotion) {
      translateY.setValue(0);
      sheetOpacity.setValue(0);
    } else {
      translateY.setValue(offscreenOffsetRef.current);
      sheetOpacity.setValue(1);
    }
    setSheetPhase('opening');
    setMounted(true);
  }, [
    backdropOpacity,
    clearFallbackTimer,
    close,
    disabled,
    reduceMotion,
    setSheetPhase,
    sheetOpacity,
    translateY,
  ]);

  // Enter animation, once the sheet is in the tree.
  useEffect(() => {
    if (phase !== 'opening') {
      return;
    }
    const generation = generationRef.current;
    const easing = EASE_OUT;
    const animations = reduceMotion
      ? [
          Animated.timing(backdropOpacity, {
            toValue: 1,
            duration: REDUCED_MOTION_FADE_MS,
            easing,
            useNativeDriver: USE_NATIVE_DRIVER,
          }),
          Animated.timing(sheetOpacity, {
            toValue: 1,
            duration: REDUCED_MOTION_FADE_MS,
            easing,
            useNativeDriver: USE_NATIVE_DRIVER,
          }),
        ]
      : [
          Animated.timing(backdropOpacity, {
            toValue: 1,
            duration: BACKDROP_ENTER_MS,
            easing,
            useNativeDriver: USE_NATIVE_DRIVER,
          }),
          Animated.timing(translateY, {
            toValue: 0,
            duration: SHEET_ENTER_MS,
            easing,
            useNativeDriver: USE_NATIVE_DRIVER,
          }),
        ];
    Animated.parallel(animations).start(({ finished }) => {
      if (
        finished &&
        generation === generationRef.current &&
        phaseRef.current === 'opening'
      ) {
        setSheetPhase('open');
      }
    });
  }, [
    backdropOpacity,
    phase,
    reduceMotion,
    setSheetPhase,
    sheetOpacity,
    translateY,
  ]);

  // Web: move focus into the sheet once it has arrived.
  useEffect(() => {
    if (!isWeb || phase !== 'open') {
      return;
    }
    const panel = panelRef.current as unknown as HTMLElement | null;
    const firstItem = panel?.querySelector?.<HTMLElement>(
      '[tabindex]:not([tabindex="-1"])',
    );
    (firstItem ?? panel)?.focus?.();
  }, [isWeb, phase]);

  // iOS only: the Modal is fully gone, so the action may present its own.
  const handleModalDismiss = useCallback(() => {
    settleClose(generationRef.current);
  }, [settleClose]);

  // Escape closes the web sheet, like any other dialog on the page.
  useEffect(() => {
    if (!isWeb || !mounted || typeof document === 'undefined') {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
        return;
      }
      // Keep Tab inside the sheet: the page behind the backdrop, Send to
      // Vertiv included, must not be reachable while it is up.
      if (event.key === 'Tab') {
        const panel = panelRef.current as unknown as HTMLElement | null;
        const focusables = panel?.querySelectorAll?.<HTMLElement>(
          '[tabindex]:not([tabindex="-1"])',
        );
        if (!panel || !focusables || focusables.length === 0) {
          event.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (!panel.contains(active)) {
          event.preventDefault();
          first.focus();
        } else if (event.shiftKey && active === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [close, isWeb, mounted]);

  /**
   * Every item can disappear while the sheet is up (a permission re-resolves,
   * the stage moves on). The component then renders nothing, so reset to
   * closed rather than keep a phase and listeners for a sheet that is gone
   * and would reappear, unasked, when items return.
   */
  const hasItems = items.length > 0;
  useEffect(() => {
    if (hasItems || phaseRef.current === 'closed') {
      return;
    }
    clearFallbackTimer();
    pendingActionRef.current = null;
    generationRef.current += 1;
    setSheetPhase('closed');
    setMounted(false);
  }, [clearFallbackTimer, hasItems, setSheetPhase]);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      clearFallbackTimer();
    };
  }, [clearFallbackTimer]);

  /**
   * Drag to dismiss. Claimed only on a mostly-vertical move past a small
   * threshold, so a tap on an item still reaches its Pressable. Under reduced
   * motion the sheet does not follow the finger (no slide), but a long
   * enough drag or a flick still dismisses it.
   */
  const panResponder = useMemo(
    () =>
      PanResponder.create({
        // A scrolling sheet keeps its vertical moves for the scroll; the
        // backdrop, Android back and Escape still dismiss it.
        onMoveShouldSetPanResponder: (_event, gesture) =>
          !contentOverflowsRef.current &&
          Math.abs(gesture.dy) > DRAG_CLAIM_THRESHOLD &&
          Math.abs(gesture.dy) > Math.abs(gesture.dx),
        onPanResponderMove: (_event, gesture) => {
          if (!reduceMotion && phaseRef.current === 'open') {
            translateY.setValue(Math.max(0, gesture.dy));
          }
        },
        onPanResponderRelease: (_event, gesture) => {
          if (
            gesture.dy > DRAG_DISMISS_DISTANCE ||
            gesture.vy > DRAG_DISMISS_VELOCITY
          ) {
            close();
            return;
          }
          if (!reduceMotion && phaseRef.current === 'open') {
            Animated.spring(translateY, {
              toValue: 0,
              bounciness: 0,
              speed: 20,
              useNativeDriver: USE_NATIVE_DRIVER,
            }).start();
          }
        },
        onPanResponderTerminate: () => {
          if (!reduceMotion && phaseRef.current === 'open') {
            Animated.spring(translateY, {
              toValue: 0,
              bounciness: 0,
              speed: 20,
              useNativeDriver: USE_NATIVE_DRIVER,
            }).start();
          }
        },
      }),
    [close, reduceMotion, translateY],
  );

  const handleContentLayout = useCallback((event: LayoutChangeEvent) => {
    const height = event.nativeEvent.layout.height;
    if (Number.isFinite(height) && height > 0) {
      setMeasuredContentHeight(previous =>
        Math.abs(previous - height) < 1 ? previous : height,
      );
    }
  }, []);

  if (items.length === 0) {
    return null;
  }

  const renderItem = (item: RequestToolsMenuItem) => (
    <Pressable
      key={item.key}
      style={({
        pressed,
        hovered,
      }: {
        pressed: boolean;
        hovered?: boolean;
      }) => [
        styles.item,
        !item.disabled && (pressed || (isWeb && hovered))
          ? item.destructive
            ? styles.itemPressedDestructive
            : styles.itemPressed
          : null,
        item.disabled && styles.itemDisabled,
      ]}
      onPress={() => {
        if (item.disabled) {
          return;
        }
        // The action waits for the sheet to be fully gone (see header).
        close(item.onPress);
      }}
      disabled={item.disabled}
      testID={item.testID ?? `${testID}-item-${item.key}`}
      accessibilityRole="button"
      accessibilityLabel={item.label}
      accessibilityHint={item.hint}
      accessibilityState={{ disabled: Boolean(item.disabled) }}
    >
      <View style={styles.itemIcon} accessibilityElementsHidden>
        {item.icon}
      </View>
      <View style={styles.itemText}>
        <Text
          style={[styles.itemLabel, item.destructive && styles.itemDestructive]}
        >
          {item.label}
        </Text>
        {item.hint ? <Text style={styles.itemHint}>{item.hint}</Text> : null}
      </View>
    </Pressable>
  );

  const motionStyle = reduceMotion
    ? { opacity: sheetOpacity }
    : { transform: [{ translateY }] };

  const sheet = (
    <View style={isWeb ? styles.webOverlay : styles.nativeOverlay}>
      <Animated.View
        style={[styles.backdrop, { opacity: backdropOpacity }]}
        testID={`${testID}-backdrop-layer`}
      >
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={() => close()}
          testID={`${testID}-backdrop`}
          accessibilityRole="button"
          accessibilityLabel="Close menu"
        />
      </Animated.View>
      <Animated.View
        {...(isWeb ? null : panResponder.panHandlers)}
        ref={panelRef}
        testID={`${testID}-panel`}
        role="dialog"
        aria-modal
        aria-label="More actions"
        accessibilityViewIsModal
        onAccessibilityEscape={() => close()}
        style={[
          styles.sheet,
          { height: sheetHeight, paddingBottom: bottomPadding },
          motionStyle,
        ]}
      >
        <ScrollView
          style={styles.sheetScroll}
          scrollEnabled={contentOverflows}
          showsVerticalScrollIndicator={contentOverflows}
          bounces={false}
        >
          <View onLayout={handleContentLayout} testID={`${testID}-content`}>
            {/* Native: a grabber, because the sheet drags. Web: a mouse or a
                phone browser gets no drag, so a grabber would promise a gesture
                that does nothing; an explicit close button takes its place, and
                the first heading shares its row so the sheet gets no taller. */}
            {isWeb ? (
              <View style={styles.webHeader}>
                {showHeadings ? (
                  <Text
                    style={[styles.sectionHeading, styles.webHeaderHeading]}
                  >
                    Add to request
                  </Text>
                ) : (
                  <View />
                )}
                <Pressable
                  onPress={() => close()}
                  style={({
                    pressed,
                    hovered,
                  }: {
                    pressed: boolean;
                    hovered?: boolean;
                  }) => [
                    styles.webCloseButton,
                    pressed || hovered ? styles.itemPressed : null,
                  ]}
                  testID={`${testID}-close`}
                  accessibilityRole="button"
                  accessibilityLabel="Close menu"
                >
                  <X size={20} color={colors.textPrimary} />
                </Pressable>
              </View>
            ) : (
              <>
                <View style={styles.sheetGrabber} accessibilityElementsHidden />
                {showHeadings ? (
                  <Text style={styles.sectionHeading}>Add to request</Text>
                ) : null}
              </>
            )}
            {additions.map(renderItem)}
            {showHeadings ? <View style={styles.sectionDivider} /> : null}
            {showHeadings ? (
              <Text style={styles.sectionHeading}>Request actions</Text>
            ) : null}
            {actions.map(renderItem)}
          </View>
        </ScrollView>
      </Animated.View>
    </View>
  );

  const createPortal = getCreatePortal();
  const webSheet =
    isWeb && mounted && createPortal && typeof document !== 'undefined'
      ? createPortal(sheet, document.body)
      : null;

  return (
    <>
      <View style={styles.anchor}>
        <Pressable
          ref={triggerRef}
          style={[
            styles.trigger,
            { width: size, height: size },
            disabled && styles.triggerDisabled,
          ]}
          onPress={handleTriggerPress}
          disabled={disabled}
          testID={`${testID}-trigger`}
          accessibilityRole="button"
          accessibilityLabel="More actions"
          accessibilityState={{ expanded: mounted, disabled }}
        >
          <MoreHorizontal size={20} color={colors.textPrimary} />
        </Pressable>
        {!isWeb ? (
          <Modal
            visible={mounted}
            transparent
            animationType="none"
            onRequestClose={() => close()}
            onDismiss={handleModalDismiss}
            statusBarTranslucent
          >
            {sheet}
          </Modal>
        ) : null}
      </View>
      {webSheet}
    </>
  );
};

const styles = StyleSheet.create({
  anchor: {
    position: 'relative',
    flexShrink: 0,
  },
  /** Same outline as the Close button it replaced in the row. */
  trigger: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#666',
    backgroundColor: colors.backgroundPrimary,
    ...Platform.select({
      web: { cursor: 'pointer' as const },
    }),
  },
  triggerDisabled: {
    opacity: 0.45,
  },
  nativeOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  webOverlay: {
    // RN-web accepts `fixed`; RN's type only lists the native values.
    position: 'fixed' as 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'flex-end',
    zIndex: 999998,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: colors.overlayBackdrop,
  },
  /**
   * No minWidth here: an explicit `minWidth: undefined` survives
   * StyleSheet.create and reaches Android's layout as NaN, which collapses
   * the view (the sheet rendered with correct state and content but zero
   * size).
   */
  sheet: {
    alignSelf: 'stretch',
    backgroundColor: colors.backgroundPrimary,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderWidth: SHEET_BORDER_WIDTH,
    borderBottomWidth: 0,
    borderColor: colors.borderLight,
    paddingTop: SHEET_TOP_PADDING,
    overflow: 'hidden',
    elevation: 12,
    ...Platform.select({
      web: { boxShadow: '0 -6px 24px rgba(0,0,0,0.14)' },
    }),
  },
  sheetScroll: {
    flex: 1,
  },
  sheetGrabber: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.borderMedium,
    alignSelf: 'center',
    marginTop: 8,
    marginBottom: 2,
  },
  webHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: SHEET_WEB_HEADER_HEIGHT,
    paddingRight: 4,
  },
  webHeaderHeading: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  webCloseButton: {
    width: SHEET_WEB_HEADER_HEIGHT,
    height: SHEET_WEB_HEADER_HEIGHT,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: { cursor: 'pointer' as const },
    }),
  },
  sectionHeading: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.6,
    color: colors.textMuted,
    textTransform: 'uppercase',
    paddingHorizontal: 14,
    paddingTop: 10,
    paddingBottom: 4,
  },
  sectionDivider: {
    height: 1,
    backgroundColor: colors.borderLight,
    marginTop: 6,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    minHeight: MENU_ITEM_HEIGHT,
    ...Platform.select({
      web: { cursor: 'pointer' as const },
    }),
  },
  itemPressed: {
    backgroundColor: colors.backgroundSecondary,
  },
  /**
   * The design's pink row behind a destructive item: pressed/hover only.
   * `colors.error` at 8% alpha; the theme has no tinted error surface token.
   */
  itemPressedDestructive: {
    backgroundColor: 'rgba(255,59,48,0.08)',
  },
  itemDisabled: {
    opacity: 0.45,
    ...Platform.select({
      web: { cursor: 'auto' as const },
    }),
  },
  itemIcon: {
    width: 20,
    height: 20,
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  itemText: {
    flex: 1,
    flexShrink: 1,
  },
  itemLabel: {
    fontSize: 15,
    color: colors.textPrimary,
    fontWeight: '500',
  },
  itemDestructive: {
    color: colors.error,
  },
  itemHint: {
    fontSize: 12,
    lineHeight: 16,
    color: colors.textSecondary,
    marginTop: 1,
  },
});
